import React, { Fragment } from 'react';
import Paper from '@mui/material/Paper';
import { DataTypeProvider, SortingState } from '@devexpress/dx-react-grid';
import { Grid, TableHeaderRow, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { Template, TemplatePlaceholder } from '@devexpress/dx-react-core';
import withStyles from '@mui/styles/withStyles';
import { getOfficeFromKey } from '../../util/brands';
import { withRouter } from '../../util/withRouter';
import { compose } from '../../reactApolloCompat';
import { withSearchTerm } from '../../context/searchTerm';
import { AutoSizer } from 'react-virtualized';
import LinearProgress from '@mui/material/LinearProgress';
import { joinDefined } from '../../util/strings';
import BrandDot from '../../component/table/BrandDot';
import LinkButton from '../../component/form/LinkButton';
import { CommittalIcon, DeliveryIcon, PodiumIcon, UrnIcon } from '../../component/IconIndex';
import { DISPOSAL_METHOD_OPTIONS, PLACE_OF_SERVICE_TYPE_OPTIONS } from '../funeral/details/DetailsConstants';
import { shortDateFromString } from '../../util/date';

const getRowId = row => row.ID;

const BrandFormatter = props => {
    const { LegacyKey, ID, __typename } = props.row;
    const funeralHome = getOfficeFromKey(LegacyKey);
    const { color, label } = funeralHome ? funeralHome : { color: 'grey', label: 'unsure' };
    let path = 'Funeral' !== __typename ? '/prearrangement' : '/funeral';
    path = path + `/${LegacyKey}/${ID}`;
    return (
        <Fragment>
            <LinkButton href={path}>
                <BrandDot dotColour={color} label={LegacyKey} title={label} />
            </LinkButton>
        </Fragment>
    );
};

const NameFormatter = props => {
    const { FirstName, MiddleName, Surname, PlaceOfService } = props.row;
    const { Private } = PlaceOfService || {};
    return (
        <span>
            {!!Private && (
                <small
                    style={{
                        color: 'red',
                        border: '1px solid red',
                        borderRadius: 5,
                        padding: '2px 3px 1px 3px',
                        margin: '-3px 4px -3px 0',
                        background: '#FF000010',
                        fontSize: '0.633rem'
                    }}
                >
                    PRIVATE
                </small>
            )}
            {joinDefined([(Surname || '').toUpperCase(), FirstName, MiddleName], ' ')}
        </span>
    );
};

const BDMReadyFormatter = props => {
    return <span>{Number(props.row.FuneralDataStatus) > 1 ? 'Yes' : 'No'}</span>;
};

const ConfirmedFormatter = props => {
    return <span>{!!props.row.Confirmed ? 'Yes' : 'No'}</span>;
};

const IntermentFormatter = props => {
    const { LegacyKey } = props.row;
    const funeralHome = getOfficeFromKey(LegacyKey);
    const { color } = funeralHome ? funeralHome : { color: 'grey' };
    const disposalMethod = DISPOSAL_METHOD_OPTIONS.find(obj =>
        [obj.value, obj.label].includes(props.row.Disposal.CrematedOrBuried)
    ) || { label: 'Unknown' };
    const serviceType = PLACE_OF_SERVICE_TYPE_OPTIONS.find(obj => obj.value === props.row.PlaceOfService.Type) || {
        label: 'Unknown'
    };
    return (
        <div style={{ color }}>
            {props.row.PlaceOfService &&
                props.row.PlaceOfService.Type &&
                ['No Service No Attendance', 'Graveside'].indexOf(props.row.PlaceOfService.Type) < 0 && (
                    <span title={serviceType.label}>
                        <PodiumIcon />
                    </span>
                )}
            {props.row.Disposal &&
                (('Buried' === disposalMethod.value && (
                    <span title={disposalMethod.label}>
                        <CommittalIcon />
                    </span>
                )) ||
                    ('Repatriated' === disposalMethod.value && (
                        <span title={disposalMethod.label}>
                            <DeliveryIcon />
                        </span>
                    )) ||
                    ('Body Donation' === disposalMethod.value && (
                        <span title={disposalMethod.label}>
                            <DeliveryIcon />
                        </span>
                    )) ||
                    ('Cremated' === disposalMethod.value && (
                        <span title={disposalMethod.label}>
                            <UrnIcon />
                        </span>
                    )))}
        </div>
    );
};

const ChecklistFormatter = props => {
    return <span>{Math.round(Number(props.row.AdminChecksComplete) * 100)}%</span>;
};

const DateFormatter = props => {
    return shortDateFromString(props.value);
};

const BrandTypeProvider = props => {
    return <DataTypeProvider formatterComponent={BrandFormatter} {...props} />;
};

const NameTypeProvider = props => {
    return <DataTypeProvider formatterComponent={NameFormatter} {...props} />;
};

const IntermentProvider = props => {
    return <DataTypeProvider formatterComponent={IntermentFormatter} {...props} />;
};

const ChecklistProvider = props => {
    return <DataTypeProvider formatterComponent={ChecklistFormatter} {...props} />;
};

const BDMReadyProvider = props => {
    return <DataTypeProvider formatterComponent={BDMReadyFormatter} {...props} />;
};

const ConfirmedTypeProvider = props => {
    return <DataTypeProvider formatterComponent={ConfirmedFormatter} {...props} />;
};

const DateTypeProvider = props => {
    return <DataTypeProvider formatterComponent={DateFormatter} {...props} />;
};

const CustomTableRowBase = ({ classes, row, onRowClick, ...restProps }) => {
    const handleClick = e => {
        const cellText = document.getSelection();
        if (cellText.type === 'Range') e.stopPropagation();
        else onRowClick(row);
    };
    return <VirtualTable.Row {...restProps} className={classes.hoverRow} onClick={handleClick} />;
};

const isColumnSortingApplied = (columnName, sorting) =>
    sorting.findIndex(sortingItem => sortingItem.field === columnName) > -1;

class SearchTable extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            funColumns: [
                //{ name: 'Type', title: 'Office' },
                { name: 'LegacyKey', title: 'Key' },
                { name: 'Name', title: 'Name' },
                { name: 'DateOfBirth', title: 'Date of Birth' },
                { name: 'DateOfService', title: 'Date of Funeral' },
                { name: 'Disposal.CrematedOrBuried', title: 'Services' },
                { name: 'Confirmed', title: 'Confirmed' },
                { name: 'AdminChecksComplete', title: 'Checklist' },
                { name: 'ExportDate', title: 'BDM sent' }
                // { name: 'ExportStatus', title: 'BDM status' }
            ],
            preColumns: [
                // { name: 'Type', title: 'Office' },
                { name: 'LegacyKey', title: 'Key' },
                { name: 'Name', title: 'Name' },
                { name: 'DateOfBirth', title: 'Date of Birth' },
                { name: 'Created', title: 'Created' },
                { name: 'Disposal.CrematedOrBuried', title: 'Services' },
                { name: 'FuneralFundName', title: 'Prepaid Fund' }
            ],
            // sorting: ,
            sortingStateColumnExtensions: [
                { columnName: 'Disposal.CrematedOrBuried', sortingEnabled: false },
                { columnName: 'LegacyKey', sortingEnabled: true },
                { columnName: 'Name', sortingEnabled: true },
                { columnName: 'DateOfBirth', sortingEnabled: true },
                { columnName: 'DateOfService', sortingEnabled: true },
                { columnName: 'Confirmed', sortingEnabled: false },
                { columnName: 'AdminChecksComplete', sortingEnabled: true },
                { columnName: 'ExportDate', sortingEnabled: true },
                { columnName: 'ExportStatus', sortingEnabled: false },
                { columnName: 'FuneralFundName', sortingEnabled: false }
            ]
        };

        this.changeSorting = sorting => {
            const { columnName } = sorting[0];
            if (
                !(columnName === 'LegacyKey' || columnName === 'Name') &&
                !isColumnSortingApplied(columnName, this.props.sortBy)
            ) {
                sorting[0].direction = 'desc';
            }

            let sortBy = [];
            for (let s of sorting) {
                if (s.columnName === 'Name') {
                    sortBy.push({ field: 'Surname', direction: s.direction === 'asc' ? 'ASC' : 'DESC' });
                    sortBy.push({ field: 'FirstName', direction: s.direction === 'asc' ? 'ASC' : 'DESC' });
                    /*
                } else if (s.columnName === 'LegacyKey') {
                    sortBy.push({ field: 'LastEdited', direction: s.direction === 'asc' ? 'ASC' : 'DESC' });
                    */
                } else {
                    sortBy.push({ field: s.columnName, direction: s.direction === 'asc' ? 'ASC' : 'DESC' });
                }
            }
            this.props.setSortBy(sortBy);
        };
    }

    getSortingForGrid() {
        let sortBy = [];

        for (let s of this.props.sortBy) {
            // if (s.field === 'LastEdited') {
            //     sortBy.push({ columnName: 'LegacyKey', direction: s.direction === 'ASC' ? 'asc' : 'desc' });
            // } else
            if (s.field === 'Surname') {
                sortBy.push({ columnName: 'Name', direction: s.direction === 'ASC' ? 'asc' : 'desc' });
            } else {
                sortBy.push({ columnName: s.field, direction: s.direction === 'ASC' ? 'asc' : 'desc' });
            }
        }

        return sortBy;
    }

    handleScrolling = topRowId => {
        const { results, onLoadMore, loading } = this.props;
        if (loading) return;
        const topIndex = results.findIndex(r => r.ID === topRowId);
        if (topIndex === -1) return;
        if (results.length - topIndex < 30) {
            onLoadMore();
        }
    };

    render() {
        const { classes, history, results, loading, prearrangements, match } = this.props;
        const { preColumns, funColumns } = this.state;
        const isRecent = 'recent' === match.params.brand;
        const tableColumnExtensions = [
            { columnName: 'LegacyKey', width: 150 },
            { columnName: 'Name', wordWrapEnabled: true },
            { columnName: 'DateOfBirth', width: 140 },
            { columnName: 'DateOfService', width: 140 },
            { columnName: 'Created', width: 150 },
            { columnName: 'Disposal.CrematedOrBuried', width: 100 },
            { columnName: 'Confirmed', width: 110 },
            { columnName: 'AdminChecksComplete', width: 100 },
            { columnName: 'ExportDate', width: 130 }
        ];
        const handleRowClick = selection => {
            const selectedRow = results.filter(({ ID }) => ID === selection.ID)[0];
            let path = !!prearrangements ? '/prearrangement' : '/funeral';
            path = path + `/${selectedRow.LegacyKey}/${selectedRow.ID}`;
            if (history.location.pathname !== path) history.push(path);
        };
        return (
            <AutoSizer disableWidth>
                {({ height }) => {
                    return (
                        <Paper>
                            <Grid
                                rows={results}
                                columns={!!prearrangements ? preColumns : funColumns}
                                getRowId={getRowId}
                            >
                                <Template name="footer">
                                    <div>
                                        {/* keep existing footer template */}
                                        <TemplatePlaceholder />

                                        {/* add spinner */}
                                        {(() => {
                                            if (loading) {
                                                return <LinearProgress />;
                                            }
                                        })()}
                                    </div>
                                </Template>

                                <BrandTypeProvider for={['LegacyKey']} classes={classes} isFuneral={!prearrangements} />
                                <NameTypeProvider for={['Name']} classes={classes} />
                                <ConfirmedTypeProvider for={['Confirmed']} classes={classes} />
                                <IntermentProvider for={['Disposal.CrematedOrBuried']} classes={classes} />
                                <ChecklistProvider for={['AdminChecksComplete']} classes={classes} />
                                <BDMReadyProvider for={['FuneralDataStatus']} classes={classes} />
                                <DateTypeProvider for={['Created', 'DateOfService']} classes={classes} />

                                {(!isRecent && (
                                    <SortingState
                                        columnSortingEnabled
                                        sorting={this.getSortingForGrid()}
                                        onSortingChange={this.changeSorting}
                                        columnExtensions={this.state.sortingStateColumnExtensions}
                                    />
                                )) || <SortingState columnSortingEnabled={false} />}

                                <VirtualTable
                                    height={height}
                                    columnExtensions={tableColumnExtensions}
                                    rowComponent={props => <CustomTableRow {...props} onRowClick={handleRowClick} />}
                                    onTopRowChange={this.handleScrolling}
                                />
                                <TableHeaderRow showSortingControls={!isRecent} />
                            </Grid>
                        </Paper>
                    );
                }}
            </AutoSizer>
        );
    }
}

const styles = ({ palette }) => ({
    spinner: {
        position: 'absolute',
        left: '50%',
        transform: 'translate(-50%, 16px)',
        top: '98%'
    },
    hoverRow: {
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: palette.action.selected
        }
    }
});
export const CustomTableRow = withStyles(styles)(CustomTableRowBase);

export default compose(withSearchTerm, withRouter, withStyles(styles))(SearchTable);
